import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getDailyQuota } from '../lib/api';

// Server's per-day / per-month token & image counters. Fetched on
// mount and refreshed:
//   • whenever auth state changes (sign-in/out switches the owner key)
//   • on demand via refresh() (e.g. after a successful subscribe)
//   • via bump(snap) when an endpoint already returned the latest
//     snapshot — saves a round trip
//
// Shape (matches GET /api/deepai/quota → `usage`):
//   {
//     plan, limits: { tokensDay, tokensMonth, imagesDay, imagesMonth },
//     tokensToday, tokensMonth,
//     imagesToday, imagesMonth,
//     dailyResetMs, monthlyResetMs,
//     subscription,
//   }
const UsageCtx = createContext({
  usage: null,
  loading: true,
  refresh: async () => null,
  bump: () => {},
});

export function UsageProvider({ user, children }) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDailyQuota();
      setUsage(snap);
      return snap;
    } catch (err) {
      console.warn('[usage]', err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch on auth flip — owner key changes between Bearer (email)
  // and X-Anon-Uid (anon UID).
  useEffect(() => { refresh(); }, [refresh, user?.email]);

  // Inline snapshot update from a chat/image response. Idempotent —
  // accepting null/undefined is a no-op so callers don't need to
  // branch on whether the server included a usage block.
  const bump = useCallback((snap) => {
    if (snap && typeof snap === 'object') setUsage(snap);
  }, []);

  return (
    <UsageCtx.Provider value={{ usage, loading, refresh, bump }}>
      {children}
    </UsageCtx.Provider>
  );
}

export const useUsage = () => useContext(UsageCtx);
